import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Badge,
    Breadcrumb,
    Button,
    Card,
    Col,
    Collapse,
    Form,
    Modal,
    Row,
    Spinner,
    Table,
    Toast,
    ToastContainer,
} from 'react-bootstrap';
import axios from 'axios';
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom';
import config from '../../config';
import { useAuth } from '../../context/AuthContext';
import '../../assets/css/performance-review.css';

const SCORE_OPTIONS = [1, 2, 3, 4, 5];
const HR_ROLES = ['SuperAdmin', 'Admin', 'HumanResource', 'Founder'];

const classificationClass = (label) => {
    if (!label) return '';
    if (label.includes('Leader')) return 'is-leader';
    if (label.includes('High')) return 'is-high';
    if (label.includes('Solid')) return 'is-solid';
    if (label.includes('Action')) return 'is-action';
    return 'is-critical';
};

const PerformanceReviewForm = ({ readOnly = false, employeeView = false }) => {
    const { id } = useParams();
    const { accessToken, userId, role } = useAuth();
    const navigate = useNavigate();
    const isHr = HR_ROLES.includes(role);

    const [catalog, setCatalog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [completing, setCompleting] = useState(false);
    const [acknowledging, setAcknowledging] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const [reviewId, setReviewId] = useState(id);
    const [status, setStatus] = useState('draft');
    const [employeeId, setEmployeeId] = useState('');
    const [periodStart, setPeriodStart] = useState('');
    const [periodEnd, setPeriodEnd] = useState('');
    const [roleTitle, setRoleTitle] = useState('');
    const [metricScores, setMetricScores] = useState([]);
    const [phasesSnapshot, setPhasesSnapshot] = useState([]);
    const [templateName, setTemplateName] = useState('');
    const [actionPlan, setActionPlan] = useState({
        repeated_mistakes: '',
        attendance_disruptions: '',
        behavioral_adjustments: '',
        action_item_1: '',
        action_item_2: '',
        action_item_3: '',
    });
    const [employeeName, setEmployeeName] = useState('');
    const [managerRemark, setManagerRemark] = useState('');
    const [savedManagerRemark, setSavedManagerRemark] = useState(null);
    const [managerRemarkAuthor, setManagerRemarkAuthor] = useState(null);
    const [managerRemarkAt, setManagerRemarkAt] = useState(null);
    const [permissions, setPermissions] = useState({});
    const [savingRemark, setSavingRemark] = useState(false);
    const [originalScores, setOriginalScores] = useState({});
    const [changeReasons, setChangeReasons] = useState({});
    const [historyOpen, setHistoryOpen] = useState({});
    const [managerRemarkHistory, setManagerRemarkHistory] = useState([]);
    const [reasonModal, setReasonModal] = useState(null);

    const headers = useMemo(
        () => ({ Authorization: `Bearer ${accessToken}` }),
        [accessToken]
    );

    const applyReview = (review) => {
        setReviewId(review.review_id);
        setStatus(review.status);
        setEmployeeId(String(review.employee_id));
        setPeriodStart(review.review_period_start);
        setPeriodEnd(review.review_period_end);
        setRoleTitle(review.role_title || '');
        setEmployeeName(review.employee?.name || '');
        setMetricScores(review.metric_scores || []);
        setPhasesSnapshot(review.phases_snapshot || []);
        setTemplateName(review.template_name || '');
        setActionPlan({
            repeated_mistakes: review.action_plan?.repeated_mistakes || '',
            attendance_disruptions: review.action_plan?.attendance_disruptions || '',
            behavioral_adjustments: review.action_plan?.behavioral_adjustments || '',
            action_item_1: review.action_plan?.action_item_1 || '',
            action_item_2: review.action_plan?.action_item_2 || '',
            action_item_3: review.action_plan?.action_item_3 || '',
        });
        setSavedManagerRemark(review.manager_remark || null);
        setManagerRemark(review.manager_remark || '');
        setManagerRemarkAuthor(review.manager_remark_author || null);
        setManagerRemarkAt(review.manager_remark_at || null);
        setManagerRemarkHistory(review.manager_remark_history || []);
        setPermissions(review.permissions || {});
        const scoreMap = {};
        (review.metric_scores || []).forEach((row) => {
            scoreMap[row.metric_key] = row.score != null ? Number(row.score) : null;
        });
        setOriginalScores(scoreMap);
        setChangeReasons({});
    };

    const loadReview = useCallback(async () => {
        if (!id) return;
        const res = await axios.get(`${config.apiBASEURL}/performance-review/${id}`, { headers });
        applyReview(res.data);
    }, [headers, id]);

    useEffect(() => {
        if (!accessToken || !id) return;
        const init = async () => {
            setLoading(true);
            setError('');
            try {
                const [catalogRes] = await Promise.all([
                    axios.get(`${config.apiBASEURL}/performance-review/metrics/catalog`, { headers }),
                    loadReview(),
                ]);
                setCatalog(catalogRes.data);
            } catch (err) {
                setError(err.response?.data?.message || 'Could not load review');
            } finally {
                setLoading(false);
            }
        };
        init();
    }, [accessToken, headers, id, loadReview]);

    const liveScoring = useMemo(() => {
        const phaseRows = (phasesSnapshot || []).map((phase) => {
            const phaseMetrics = phase.metrics || [];
            const rows = metricScores.filter((m) =>
                phaseMetrics.some((pm) => pm.metric_key === m.metric_key) || m.phase === phase.id
            );
            const scores = rows.map((r) => r.score).filter((s) => s != null);
            const avg = scores.length
                ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 100) / 100
                : null;
            const weighted = avg != null ? Math.round(avg * Number(phase.weight) * 100) / 100 : 0;
            return {
                id: phase.id,
                label: phase.label,
                weight: Number(phase.weight),
                avg,
                weighted,
                scored: scores.length,
                total: phaseMetrics.length || rows.length,
            };
        });
        const finalScore = phaseRows.some((p) => p.scored > 0)
            ? Math.round(phaseRows.reduce((s, p) => s + p.weighted, 0) * 100) / 100
            : null;
        let classification = null;
        if (finalScore != null) {
            if (finalScore >= 4.5) classification = 'Agency Leader';
            else if (finalScore >= 3.5) classification = 'High Performer';
            else if (finalScore >= 3.0) classification = 'Solid Contributor';
            else if (finalScore >= 2.0) classification = 'Action Required';
            else classification = 'Immediate Review';
        }
        return { phaseRows, finalScore, classification };
    }, [phasesSnapshot, metricScores]);

    const isSelfReviewer = Number(userId) === Number(employeeId);
    const statusLocked = ['completed', 'acknowledged'].includes(status);
    const periodOpen = useMemo(() => {
        if (permissions.period_open === false) return false;
        if (permissions.period_open === true) return true;
        if (!periodStart) return true;
        return moment().isSameOrAfter(moment(periodStart), 'day');
    }, [permissions.period_open, periodStart]);
    const canEdit = !readOnly && isSelfReviewer && !statusLocked && reviewId && periodOpen;
    const canChangeScores =
        !readOnly &&
        (permissions.can_change_scores
            || permissions.can_hr_override_scores
            || permissions.can_manager_override_scores
            || (isHr && !employeeView && !isSelfReviewer && !!reviewId && statusLocked));
    const canEditReviewerRemarks =
        !readOnly &&
        (permissions.can_edit_reviewer_remarks
            || canChangeScores
            || (permissions.is_manager_viewer && statusLocked));
    const canSubmitManagerRemark =
        !readOnly && !!permissions.can_submit_manager_remark;
    const canViewManagerRemark =
        Boolean(permissions.can_view_manager_remark)
        || isHr
        || canSubmitManagerRemark
        || Boolean(savedManagerRemark && (isHr || permissions.is_manager_viewer));
    const isLocked = readOnly || !(canEdit || canChangeScores);
    const remarksReadOnly = readOnly || !canEditReviewerRemarks;
    const canAcknowledgeAdmin = !employeeView && isHr && status === 'completed';
    const pipRequired = liveScoring.finalScore != null && liveScoring.finalScore < 3.0;

    const listPath = employeeView || isSelfReviewer || canSubmitManagerRemark || permissions.is_manager_viewer
        ? '/Graphe/dashboard/my_performance_reviews'
        : '/Graphe/dashboard/performance_reviews';

    const updateMetric = (metricKey, field, value) => {
        setMetricScores((prev) =>
            prev.map((row) =>
                row.metric_key === metricKey ? { ...row, [field]: value } : row
            )
        );
    };

    const requestScoreChange = (metricKey, nextScore) => {
        if (!canChangeScores) {
            updateMetric(metricKey, 'score', nextScore);
            return;
        }
        const prev = originalScores[metricKey];
        const normalized = nextScore === '' || nextScore == null ? null : Number(nextScore);
        const prevNorm = prev == null || prev === '' ? null : Number(prev);
        if (normalized === prevNorm) {
            updateMetric(metricKey, 'score', normalized);
            setChangeReasons((r) => {
                const next = { ...r };
                delete next[metricKey];
                return next;
            });
            return;
        }
        setReasonModal({
            metricKey,
            nextScore: normalized,
            oldScore: prevNorm,
            reason: changeReasons[metricKey] || '',
        });
    };

    const confirmScoreChangeReason = () => {
        if (!reasonModal) return;
        const reason = (reasonModal.reason || '').trim();
        if (reason.length < 3) {
            setError('Enter a valid reason (at least 3 characters) for each score change.');
            return;
        }
        updateMetric(reasonModal.metricKey, 'score', reasonModal.nextScore);
        setChangeReasons((prev) => ({ ...prev, [reasonModal.metricKey]: reason }));
        setReasonModal(null);
        setError('');
    };

    const buildPayload = () => ({
        review_period_start: periodStart,
        review_period_end: periodEnd,
        role_title: roleTitle,
        status: status === 'draft' ? 'in_progress' : status,
        metric_scores: metricScores.map((row) => {
            const payload = {
                metric_key: row.metric_key,
                score: row.score != null ? Number(row.score) : null,
                notes: row.notes || '',
                reviewer_remark: row.reviewer_remark || '',
            };
            const prev = originalScores[row.metric_key];
            const prevNorm = prev == null || prev === '' ? null : Number(prev);
            const nextNorm = row.score != null ? Number(row.score) : null;
            if (canChangeScores && prevNorm !== nextNorm && changeReasons[row.metric_key]) {
                payload.change_reason = changeReasons[row.metric_key];
            }
            return payload;
        }),
        action_plan: actionPlan,
    });

    const validateOverrideReasons = () => {
        if (!canChangeScores) return true;
        const missing = metricScores.filter((row) => {
            const prev = originalScores[row.metric_key];
            const prevNorm = prev == null || prev === '' ? null : Number(prev);
            const nextNorm = row.score != null ? Number(row.score) : null;
            return prevNorm !== nextNorm && !(changeReasons[row.metric_key] || '').trim();
        });
        if (missing.length) {
            setError('Provide a reason for every score you changed.');
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        if (!periodOpen && isSelfReviewer) {
            setError(`This review opens on ${moment(periodStart).format('DD MMM YYYY')}. You cannot fill it until then.`);
            return;
        }
        setSaving(true);
        setError('');
        setMessage('');
        try {
            const res = await axios.put(
                `${config.apiBASEURL}/performance-review/${reviewId}`,
                buildPayload(),
                { headers }
            );
            applyReview(res.data);
            setMessage('Review saved.');
        } catch (err) {
            setError(err.response?.data?.message || 'Could not save review');
        } finally {
            setSaving(false);
        }
    };

    const handleComplete = async () => {
        if (!periodOpen && isSelfReviewer) {
            setError(`This review opens on ${moment(periodStart).format('DD MMM YYYY')}. You cannot submit it until then.`);
            return;
        }
        setCompleting(true);
        setError('');
        setMessage('');
        try {
            await axios.put(
                `${config.apiBASEURL}/performance-review/${reviewId}`,
                buildPayload(),
                { headers }
            );
            const res = await axios.patch(
                `${config.apiBASEURL}/performance-review/${reviewId}/complete`,
                {},
                { headers }
            );
            applyReview(res.data);
            setMessage('Self-review submitted.');
        } catch (err) {
            const errors = err.response?.data?.errors;
            setError(
                errors?.length
                    ? errors.join(' ')
                    : err.response?.data?.message || 'Could not submit review'
            );
        } finally {
            setCompleting(false);
        }
    };

    const handleAcknowledge = async () => {
        setAcknowledging(true);
        setError('');
        try {
            const res = await axios.patch(
                `${config.apiBASEURL}/performance-review/${reviewId}/acknowledge`,
                {},
                { headers }
            );
            applyReview(res.data);
            setMessage('Review acknowledged.');
        } catch (err) {
            setError(err.response?.data?.message || 'Could not acknowledge review');
        } finally {
            setAcknowledging(false);
        }
    };

    // HR / manager can revise scores after submit — each change needs a reason.
    const handleScoreOverrideSave = async () => {
        if (!validateOverrideReasons()) return;
        setSaving(true);
        setError('');
        setMessage('');
        try {
            const res = await axios.put(
                `${config.apiBASEURL}/performance-review/${reviewId}`,
                buildPayload(),
                { headers }
            );
            applyReview(res.data);
            setMessage('Scores and remarks updated.');
        } catch (err) {
            setError(err.response?.data?.message || 'Could not update scores');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveManagerRemark = async () => {
        setSavingRemark(true);
        setError('');
        setMessage('');
        try {
            const res = await axios.patch(
                `${config.apiBASEURL}/performance-review/${reviewId}/manager-remark`,
                { remark: managerRemark },
                { headers }
            );
            applyReview(res.data);
            setMessage('Manager remark submitted.');
        } catch (err) {
            setError(err.response?.data?.message || 'Could not save remark');
        } finally {
            setSavingRemark(false);
        }
    };

    const metricsByPhase = useMemo(
        () =>
            (phasesSnapshot || []).map((phase) => ({
                phase,
                metrics: (phase.metrics || []).map((metric) => {
                    const row = metricScores.find((s) => s.metric_key === metric.metric_key);
                    return { metric, row };
                }),
            })),
        [phasesSnapshot, metricScores]
    );

    if (loading) {
        return (
            <Row className="body_content">
                <Col md={12} className="text-center py-5">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading review…
                </Col>
            </Row>
        );
    }

    return (
        <div className="pr-form-page">
            <ToastContainer position="bottom-end" className="p-3" style={{ position: 'fixed', zIndex: 1080 }}>
                <Toast bg="danger" show={!!error} onClose={() => setError('')} delay={5000} autohide>
                    <Toast.Header closeButton>
                        <strong className="me-auto">Error</strong>
                    </Toast.Header>
                    <Toast.Body className="text-white">{error}</Toast.Body>
                </Toast>
                <Toast bg="success" show={!!message} onClose={() => setMessage('')} delay={4000} autohide>
                    <Toast.Header closeButton>
                        <strong className="me-auto">Success</strong>
                    </Toast.Header>
                    <Toast.Body className="text-white">{message}</Toast.Body>
                </Toast>
            </ToastContainer>

            <Row className="body_content g-3">
                <Col md={12} className="mb-3">
                    <Breadcrumb>
                        <Breadcrumb.Item style={{ cursor: 'pointer' }} onClick={() => navigate(listPath)}>
                            Performance Reviews
                        </Breadcrumb.Item>
                        <Breadcrumb.Item active>{employeeName || 'Review'}</Breadcrumb.Item>
                    </Breadcrumb>
                </Col>

                {isHr && !isSelfReviewer && (
                    <Col md={12}>
                        <Alert variant="info" className="mb-0">
                            {status === 'completed'
                                ? 'Employee has submitted their self-review. You can acknowledge it, adjust scores (reason required), and add reviewer remarks per metric.'
                                : status === 'acknowledged'
                                    ? 'This review has been acknowledged. You can still adjust scores (with a reason) and update reviewer remarks.'
                                    : 'Employee self-review in progress. Fields show their responses when submitted.'}
                        </Alert>
                    </Col>
                )}

                {canChangeScores && !isHr && (
                    <Col md={12}>
                        <Alert variant="info" className="mb-0">
                            As the reporting manager you can adjust scores (a reason is required for each change) and add reviewer remarks per line item.
                        </Alert>
                    </Col>
                )}

                {canSubmitManagerRemark && !isHr && !canChangeScores && (
                    <Col md={12}>
                        <Alert variant="info" className="mb-0">
                            This report submitted a self-review. Add your manager remark below.
                        </Alert>
                    </Col>
                )}

                {isSelfReviewer && !periodOpen && !statusLocked && (
                    <Col md={12}>
                        <Alert variant="warning" className="mb-0">
                            This review opens on{' '}
                            <strong>{moment(periodStart).format('DD MMM YYYY')}</strong>.
                            {' '}You can fill and submit it once the review period starts.
                        </Alert>
                    </Col>
                )}

                <Col lg={8}>
                    <Card className="mb-3">
                        <Card.Body>
                            <h5 className="mb-3">Review details</h5>
                            <Row className="g-3">
                                <Col md={6}>
                                    <Form.Label>Employee</Form.Label>
                                    <Form.Control value={employeeName} readOnly />
                                </Col>
                                <Col md={6}>
                                    <Form.Label>Role / Title</Form.Label>
                                    <Form.Control
                                        value={roleTitle}
                                        onChange={(e) => setRoleTitle(e.target.value)}
                                        readOnly={isLocked}
                                    />
                                </Col>
                                {templateName && (
                                    <Col md={12}>
                                        <Form.Label>Template</Form.Label>
                                        <Form.Control value={templateName} readOnly />
                                    </Col>
                                )}
                                <Col md={6}>
                                    <Form.Label>Period start</Form.Label>
                                    <Form.Control type="date" value={periodStart} readOnly />
                                </Col>
                                <Col md={6}>
                                    <Form.Label>Period end</Form.Label>
                                    <Form.Control type="date" value={periodEnd} readOnly />
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {catalog && (
                        <Card className="mb-3">
                            <Card.Body>
                                <h6 className="mb-3">Scoring guidelines</h6>
                                <Table bordered size="sm" className="pr-guidelines-table mb-0">
                                    <thead>
                                        <tr><th>Score</th><th>Label</th><th>Definition</th></tr>
                                    </thead>
                                    <tbody>
                                        {catalog.scoring_guidelines.map((g) => (
                                            <tr key={g.score}>
                                                <td><strong>{g.score}</strong></td>
                                                <td>{g.label}</td>
                                                <td>{g.description}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    )}
                    {(canViewManagerRemark || canSubmitManagerRemark) && statusLocked && (
                        <Card className="mb-3">
                            <Card.Body>
                                <h5 className="mb-1">Manager remark</h5>
                                <p className="text-muted small mb-3">
                                    {canSubmitManagerRemark
                                        ? 'Add your remark for this report’s self-review. Admins can see it on the review.'
                                        : 'Remark from the employee’s reporting manager (org hierarchy).'}
                                </p>
                                {canSubmitManagerRemark ? (
                                    <>
                                        <Form.Control
                                            as="textarea"
                                            rows={4}
                                            value={managerRemark}
                                            onChange={(e) => setManagerRemark(e.target.value)}
                                            placeholder="Enter your remark…"
                                        />
                                        <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
                                            {managerRemarkAt && (
                                                <span className="text-muted small">
                                                    Last saved
                                                    {managerRemarkAuthor?.name ? ` by ${managerRemarkAuthor.name}` : ''}
                                                    {' · '}
                                                    {new Date(managerRemarkAt).toLocaleString()}
                                                </span>
                                            )}
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={handleSaveManagerRemark}
                                                disabled={savingRemark || !managerRemark.trim()}
                                            >
                                                {savingRemark
                                                    ? 'Saving…'
                                                    : savedManagerRemark
                                                        ? 'Update remark'
                                                        : 'Submit remark'}
                                            </Button>
                                        </div>
                                    </>
                                ) : savedManagerRemark ? (
                                    <>
                                        <Form.Control as="textarea" rows={4} value={savedManagerRemark} readOnly />
                                        <div className="text-muted small mt-2">
                                            {managerRemarkAuthor?.name || 'Manager'}
                                            {managerRemarkAt
                                                ? ` · ${new Date(managerRemarkAt).toLocaleString()}`
                                                : ''}
                                        </div>
                                    </>
                                ) : (
                                    <Alert variant="secondary" className="mb-0 py-2 small">
                                        No manager remark submitted yet.
                                    </Alert>
                                )}
                                {managerRemarkHistory.length > 0 && (
                                    <div className="mt-3">
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="p-0"
                                            onClick={() =>
                                                setHistoryOpen((prev) => ({
                                                    ...prev,
                                                    'manager-remark': !prev['manager-remark'],
                                                }))
                                            }
                                        >
                                            {historyOpen['manager-remark'] ? 'Hide' : 'Show'} manager remark history ({managerRemarkHistory.length})
                                        </Button>
                                        <Collapse in={!!historyOpen['manager-remark']}>
                                            <div className="mt-2">
                                                <Table bordered size="sm" className="mb-0">
                                                    <thead>
                                                        <tr>
                                                            <th>When</th>
                                                            <th>Previous</th>
                                                            <th>Updated</th>
                                                            <th>By</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {managerRemarkHistory.map((h) => (
                                                            <tr key={h.history_id}>
                                                                <td className="small text-nowrap">
                                                                    {h.changed_at
                                                                        ? new Date(h.changed_at).toLocaleString()
                                                                        : '—'}
                                                                </td>
                                                                <td className="small">{h.old_remark || '—'}</td>
                                                                <td className="small">{h.new_remark || '—'}</td>
                                                                <td className="small">
                                                                    {h.changed_by?.name || '—'}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </Collapse>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {metricsByPhase.map(({ phase, metrics }) => (
                        <div key={phase.id} className="pr-phase-card">
                            <div className="pr-phase-header">
                                <div>
                                    <strong>Phase {phase.id}: {phase.label}</strong>
                                    <div className="pr-phase-score-pill mt-1">
                                        Subtotal avg:{' '}
                                        {liveScoring.phaseRows.find((p) => p.id === phase.id)?.avg ?? '—'}
                                    </div>
                                </div>
                                <span className="pr-phase-weight">
                                    Weight {(Number(phase.weight) * 100).toFixed(0)}%
                                </span>
                            </div>
                            {metrics.map(({ metric, row }) => {
                                const history = row?.score_history || [];
                                const remarkHistory = row?.remark_history || [];
                                const historyKey = metric.metric_key;
                                const remarkHistoryKey = `remark-${metric.metric_key}`;
                                const pendingReason = changeReasons[metric.metric_key];
                                return (
                                <div key={metric.metric_key} className="pr-metric-row">
                                    <div className="d-flex justify-content-between gap-3 flex-wrap">
                                        <div className="flex-grow-1">
                                            <strong>
                                                {metric.number != null ? `#${metric.number} ` : ''}
                                                {metric.name}
                                            </strong>
                                            {metric.target && (
                                                <div className="pr-metric-target">{metric.target}</div>
                                            )}
                                        </div>
                                        <Form.Select
                                            className="pr-score-select"
                                            value={row?.score ?? ''}
                                            disabled={isLocked}
                                            onChange={(e) => {
                                                const val = e.target.value ? Number(e.target.value) : null;
                                                if (canChangeScores) {
                                                    requestScoreChange(metric.metric_key, val);
                                                } else {
                                                    updateMetric(metric.metric_key, 'score', val);
                                                }
                                            }}
                                        >
                                            <option value="">Score</option>
                                            {SCORE_OPTIONS.map((s) => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                        </Form.Select>
                                    </div>
                                    {pendingReason && (
                                        <div className="text-muted small mt-1">
                                            Pending change reason: {pendingReason}
                                        </div>
                                    )}
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        className="mt-2"
                                        placeholder="Employee notes"
                                        value={row?.notes || ''}
                                        readOnly={isLocked || canChangeScores}
                                        onChange={(e) => updateMetric(metric.metric_key, 'notes', e.target.value)}
                                    />
                                    <Form.Label className="small mt-2 mb-1">Reviewer remark</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        placeholder={
                                            canEditReviewerRemarks
                                                ? 'Add a reviewer remark for this metric…'
                                                : 'No reviewer remark'
                                        }
                                        value={row?.reviewer_remark || ''}
                                        readOnly={remarksReadOnly}
                                        onChange={(e) =>
                                            updateMetric(metric.metric_key, 'reviewer_remark', e.target.value)
                                        }
                                    />
                                    {remarkHistory.length > 0 && (
                                        <div className="mt-2">
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="p-0"
                                                onClick={() =>
                                                    setHistoryOpen((prev) => ({
                                                        ...prev,
                                                        [remarkHistoryKey]: !prev[remarkHistoryKey],
                                                    }))
                                                }
                                            >
                                                {historyOpen[remarkHistoryKey] ? 'Hide' : 'Show'} remark history ({remarkHistory.length})
                                            </Button>
                                            <Collapse in={!!historyOpen[remarkHistoryKey]}>
                                                <div className="mt-2">
                                                    <Table bordered size="sm" className="mb-0">
                                                        <thead>
                                                            <tr>
                                                                <th>When</th>
                                                                <th>Previous</th>
                                                                <th>Updated</th>
                                                                <th>By</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {remarkHistory.map((h) => (
                                                                <tr key={h.history_id}>
                                                                    <td className="small text-nowrap">
                                                                        {h.changed_at
                                                                            ? new Date(h.changed_at).toLocaleString()
                                                                            : '—'}
                                                                    </td>
                                                                    <td className="small">{h.old_remark || '—'}</td>
                                                                    <td className="small">{h.new_remark || '—'}</td>
                                                                    <td className="small">
                                                                        {h.changed_by?.name || '—'}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </Collapse>
                                        </div>
                                    )}
                                    {history.length > 0 && (
                                        <div className="mt-2">
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="p-0"
                                                onClick={() =>
                                                    setHistoryOpen((prev) => ({
                                                        ...prev,
                                                        [historyKey]: !prev[historyKey],
                                                    }))
                                                }
                                            >
                                                {historyOpen[historyKey] ? 'Hide' : 'Show'} score history ({history.length})
                                            </Button>
                                            <Collapse in={!!historyOpen[historyKey]}>
                                                <div className="mt-2">
                                                    <Table bordered size="sm" className="mb-0">
                                                        <thead>
                                                            <tr>
                                                                <th>When</th>
                                                                <th>Old</th>
                                                                <th>New</th>
                                                                <th>By</th>
                                                                <th>Reason</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {history.map((h) => (
                                                                <tr key={h.history_id}>
                                                                    <td className="small text-nowrap">
                                                                        {h.changed_at
                                                                            ? new Date(h.changed_at).toLocaleString()
                                                                            : '—'}
                                                                    </td>
                                                                    <td>{h.old_score ?? '—'}</td>
                                                                    <td>{h.new_score ?? '—'}</td>
                                                                    <td className="small">
                                                                        {h.changed_by?.name || '—'}
                                                                    </td>
                                                                    <td className="small">{h.reason}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </Collapse>
                                        </div>
                                    )}
                                </div>
                                );
                            })}
                        </div>
                    ))}

                    <Card className="mb-3">
                        <Card.Body>
                            <h5 className="mb-3">Qualitative audit & action plan</h5>
                            <Form.Group className="mb-3">
                                <Form.Label>Repeated mistakes identified</Form.Label>
                                <Form.Control as="textarea" rows={2} value={actionPlan.repeated_mistakes} readOnly={isLocked} onChange={(e) => setActionPlan((p) => ({ ...p, repeated_mistakes: e.target.value }))} />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Attendance disruptions</Form.Label>
                                <Form.Control as="textarea" rows={2} value={actionPlan.attendance_disruptions} readOnly={isLocked} onChange={(e) => setActionPlan((p) => ({ ...p, attendance_disruptions: e.target.value }))} />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Behavioral adjustments required</Form.Label>
                                <Form.Control as="textarea" rows={2} value={actionPlan.behavioral_adjustments} readOnly={isLocked} onChange={(e) => setActionPlan((p) => ({ ...p, behavioral_adjustments: e.target.value }))} />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>30-day action item 1 {pipRequired && canEdit && <span className="text-danger">*</span>}</Form.Label>
                                <Form.Control as="textarea" rows={2} value={actionPlan.action_item_1} readOnly={isLocked} onChange={(e) => setActionPlan((p) => ({ ...p, action_item_1: e.target.value }))} />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>30-day action item 2 {pipRequired && canEdit && <span className="text-danger">*</span>}</Form.Label>
                                <Form.Control as="textarea" rows={2} value={actionPlan.action_item_2} readOnly={isLocked} onChange={(e) => setActionPlan((p) => ({ ...p, action_item_2: e.target.value }))} />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>30-day action item 3 {pipRequired && canEdit && <span className="text-danger">*</span>}</Form.Label>
                                <Form.Control as="textarea" rows={2} value={actionPlan.action_item_3} readOnly={isLocked} onChange={(e) => setActionPlan((p) => ({ ...p, action_item_3: e.target.value }))} />
                            </Form.Group>
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={4} className="pr-form-sidebar">
                    <div className="pr-form-sidebar-sticky">
                        <Card className="pr-score-card mb-0">
                            <Card.Body>
                                <div className="text-muted small mb-1">Final comprehensive score</div>
                                <div className="pr-final-score">{liveScoring.finalScore ?? '—'}</div>
                                {liveScoring.classification && (
                                    <div className="mt-2">
                                        <span className={`pr-classification-badge ${classificationClass(liveScoring.classification)}`}>
                                            {liveScoring.classification}
                                        </span>
                                    </div>
                                )}
                                <hr className="my-2" />
                                <div className="small text-muted mb-2">Weighted phase scores</div>
                                <div className="pr-phase-score-list">
                                    {liveScoring.phaseRows.map((phase) => (
                                        <div key={phase.id} className="pr-phase-score-row">
                                            <span className="pr-phase-score-label">{phase.label || `Phase ${phase.id}`}</span>
                                            <span className="pr-phase-score-value">
                                                {phase.avg ?? '—'} × {(phase.weight * 100).toFixed(0)}% = {phase.weighted || 0}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                <hr className="my-2" />
                                <Badge
                                    bg={status === 'acknowledged' ? 'primary' : status === 'completed' ? 'success' : 'secondary'}
                                    className="pr-status-badge"
                                >
                                    {status.replace('_', ' ')}
                                </Badge>
                                {pipRequired && canEdit && (
                                    <Alert variant="warning" className="mt-3 mb-0 py-2 small">
                                        Score below 3.00 — all three 30-day action items are required before submitting.
                                    </Alert>
                                )}
                            </Card.Body>
                        </Card>

                        <div className="d-grid gap-2 pr-form-actions">
                            {canEdit && (
                                <>
                                    <Button variant="outline-primary" onClick={handleSave} disabled={saving}>
                                        {saving ? 'Saving…' : 'Save draft'}
                                    </Button>
                                    <Button variant="primary" onClick={handleComplete} disabled={completing || saving}>
                                        {completing ? 'Submitting…' : 'Submit self-review'}
                                    </Button>
                                </>
                            )}
                            {(canChangeScores || canEditReviewerRemarks) && (
                                <Button variant="outline-primary" onClick={handleScoreOverrideSave} disabled={saving}>
                                    {saving ? 'Saving…' : 'Update scores / remarks'}
                                </Button>
                            )}
                            {canAcknowledgeAdmin && (
                                <Button variant="success" onClick={handleAcknowledge} disabled={acknowledging}>
                                    {acknowledging ? 'Acknowledging…' : 'Acknowledge review'}
                                </Button>
                            )}
                            <Button variant="outline-secondary" onClick={() => navigate(listPath)}>
                                Back to list
                            </Button>
                        </div>
                    </div>
                </Col>
            </Row>

            <Modal show={!!reasonModal} onHide={() => setReasonModal(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Reason for score change</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="small text-muted mb-2">
                        Changing score from{' '}
                        <strong>{reasonModal?.oldScore ?? '—'}</strong>
                        {' '}to{' '}
                        <strong>{reasonModal?.nextScore ?? '—'}</strong>.
                        A reason is required.
                    </p>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={reasonModal?.reason || ''}
                        onChange={(e) =>
                            setReasonModal((prev) => (prev ? { ...prev, reason: e.target.value } : prev))
                        }
                        placeholder="Explain why this score is being changed…"
                        autoFocus
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={() => setReasonModal(null)}
                    >
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={confirmScoreChangeReason}>
                        Apply change
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PerformanceReviewForm;
